import type { InstrumentName } from '@tunasong/audio-ui'
import { MelodyLib } from '@tunasong/music-lib'
import type { MelodyMeter, NoteName } from '@tunasong/schemas'
import type { Stab } from '../player/stab-scheduler.js'

/**
 * The click a capture is played against: two knocks, then the beat, for the whole take.
 *
 * Every rule here is a rule about what must NOT sound. The count-in is a woodblock and not the
 * melody's instrument, because a pitched cue interpolated before a performance erodes the pitch the
 * player is holding - the lesson the Pitch Trainer learned from a real user in September. There are
 * two knocks and not a bar of them for the same reason: two say "here it comes", a full bar is an
 * ensemble's "start together", and the extra beats cost pitch memory for nothing.
 *
 * The downbeat accent is VELOCITY on one pitch. `buildClickStabs` - the Rhythm Trainer's - codes its
 * accent tiers as C6 / A5 / G5, three different pitches, and that is exactly what cannot happen
 * here: three pitched tones per bar under a performance the tracker is about to segment would be
 * captured and read as notes. One pitch, two dynamics.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5
 */

/** Unpitched percussion. The click must never share a timbre with what is being played. */
export const CLICK_INSTRUMENT: InstrumentName = 'woodblock'

/**
 * The click's note name. On a woodblock the sample is unpitched, but the engine needs a note to
 * trigger it - and ONE note, for the whole track, so the accent carries no melodic information.
 */
const CLICK_NOTE = 'C5' as NoteName

/** Short enough to be a knock rather than a tone. */
const CLICK_DURATION = '32n' as const

/** Two. Enough to say "here it comes"; fewer than a bar, which would say "start together". */
export const COUNT_IN_CLICKS = 2

/** The bar's first beat. Loud enough to find without a second pitch to find it by. */
export const CLICK_ACCENT_VELOCITY = 1

/**
 * Every other beat. 0.6 rather than something quieter: the click has to stay audible under a sung or
 * strummed note, and the accent only has to be *more*, not the only thing heard.
 */
export const CLICK_VELOCITY = 0.6

export interface ClickTrackOptions {
  bpm: number
  meter: MelodyMeter
  bars: number
  /**
   * Prepend the two count-in knocks. Default true - a capture take is cued by them.
   *
   * The Melody Trainer turns them off: its count-in has already sounded, before the reference, and
   * a second one after the silent beat would read as a new start rather than as bar 1 of the same
   * continuous pulse (§6.4).
   */
  countIn?: boolean
}

/**
 * The count-in runs at the take's own tempo, unlike the Pitch Trainer's fixed 100 bpm walk. There
 * the count-in only said "listen"; here it also sets the tempo the player is about to perform to,
 * and a count-in at a different tempo from the click it hands over to would be a lie.
 */
export const countInDurationMs = (bpm: number): number => COUNT_IN_CLICKS * MelodyLib.beatMs(bpm)

export const takeDurationMs = ({ bpm, meter, bars }: ClickTrackOptions): number =>
  bars * MelodyLib.barBeats(meter) * MelodyLib.beatMs(bpm)

export const buildClickTrack = (options: ClickTrackOptions): Stab[] => {
  const { bpm, meter, bars, countIn = true } = options
  const countInMs = countIn ? countInDurationMs(bpm) : 0
  /** The METER's beat, not the quarter: in 6/8 the click is the eighth. */
  const clickMs = MelodyLib.beatMs(bpm) * (4 / meter.bottom)

  const click = (delayMs: number, velocity: number): Stab => ({
    events: [{ type: 'note' as const, note: CLICK_NOTE, duration: CLICK_DURATION, velocity }],
    delayMs,
  })

  const countInStabs = countIn
    ? Array.from({ length: COUNT_IN_CLICKS }, (_unused, index) =>
        click(index * MelodyLib.beatMs(bpm), CLICK_ACCENT_VELOCITY)
      )
    : []

  const takeStabs = Array.from({ length: bars * meter.top }, (_unused, index) =>
    click(countInMs + index * clickMs, index % meter.top === 0 ? CLICK_ACCENT_VELOCITY : CLICK_VELOCITY)
  )

  return [...countInStabs, ...takeStabs]
}
